<template>
    <div class="panel panel-default" data-testid="order-detail-returns">
        <div class="panel-heading">
            <h4>
                {{ label }}
                <span
                    v-if="returns.inProgressCount > 0"
                    class="label label-warning"
                    style="margin-left: 8px;"
                >
                    {{ returns.inProgressCount }} in lavorazione
                </span>
            </h4>
        </div>
        <div class="panel-body">
            <p v-if="errorMessage" class="alert alert-danger">{{ errorMessage }}</p>
            <p v-if="successMessage" class="alert alert-success">{{ successMessage }}</p>
            <p v-if="!returns.items.length && !giftCards.length && !substitutionLines.length" class="text-muted mb-0">
                Nessun reso collegato
            </p>
            <div
                v-for="item in returns.items"
                :key="item.returnOrderId"
                class="order-detail-v1-returns__item"
            >
                <div class="clearfix" style="margin-bottom: 8px;">
                    <code>{{ item.returnOrderId }}</code>
                    <span class="label label-warning" style="margin-left: 6px;">{{ item.resoStatusLabel }}</span>
                    <span class="text-muted" style="margin-left: 8px;">
                        Creato il {{ formatDate(item.createdAt) }}
                    </span>
                    <span class="text-muted" style="margin-left: 8px;">
                        Ordine origine <code>{{ item.originalOrderId }}</code>
                    </span>
                    <span v-if="item.amountPaidFormatted" class="text-muted" style="margin-left: 8px;">
                        Importo <strong>{{ item.amountPaidFormatted }}</strong>
                    </span>
                </div>
                <p v-if="item.waybillUrl" style="margin-bottom: 8px;">
                    <a
                        :href="item.waybillUrl"
                        class="btn btn-sm btn-default"
                        data-testid="order-detail-return-waybill"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Lettera di vettura
                    </a>
                </p>
                <ul class="list-unstyled order-detail-v1-returns__lines">
                    <li
                        v-for="line in item.lines"
                        :key="line.ean + '-' + line.matricola"
                        class="order-detail-v1-returns__line"
                    >
                        <div class="order-detail-v1-returns__gift-meta">
                            <strong>{{ line.title || 'Riga reso' }}</strong>
                            <span>×{{ line.qty }}</span>
                        </div>
                        <div v-if="line.ean"><code>{{ line.ean }}</code></div>
                        <div class="text-muted">{{ line.actLabel || '—' }}<span v-if="line.netAmountFormatted"> · {{ line.netAmountFormatted }}</span></div>
                    </li>
                </ul>
                <div v-if="item.resoStatus === 1" class="order-detail-v1-line-actions">
                    <button type="button" class="btn btn-sm btn-default" :disabled="busy" @click="openOutcome(item, 'archive', 'Archivia')">Archivia</button>
                    <button type="button" class="btn btn-sm btn-default" :disabled="busy" @click="openOutcome(item, 'refund', 'Rimborso')">Rimborso</button>
                    <button type="button" class="btn btn-sm btn-default" :disabled="busy" @click="openOutcome(item, 'gift', 'Gift')">Gift</button>
                </div>
            </div>

            <section
                v-if="giftCards.length"
                class="order-detail-v1-returns__stack"
                data-testid="order-detail-gift-cards"
            >
                <h5 class="order-detail-v1-returns__stack-title">
                    Gift card
                    <span class="pull-right text-muted">Saldo <strong>{{ giftSaldo }}</strong></span>
                </h5>
                <ul class="list-unstyled">
                    <li
                        v-for="card in giftCards"
                        :key="card.code"
                        class="order-detail-v1-returns__gift"
                    >
                        <div class="order-detail-v1-returns__gift-code"><code>{{ card.code }}</code></div>
                        <div class="order-detail-v1-returns__gift-meta">
                            <span class="label label-default">{{ giftKindLabel(card.kind) }}</span>
                            <strong>{{ card.amountFormatted || '—' }}</strong>
                        </div>
                        <div class="text-muted">Emessa il {{ formatDate(card.issuedAt) }}</div>
                    </li>
                </ul>
            </section>

            <section
                v-if="substitutionLines.length"
                class="order-detail-v1-returns__stack"
                data-testid="order-detail-substitutions"
            >
                <h5 class="order-detail-v1-returns__stack-title">Righe di sostituzione</h5>
                <ul class="list-unstyled">
                    <li
                        v-for="(line, index) in substitutionLines"
                        :key="(line.ean || 'line') + '-' + index"
                        class="order-detail-v1-returns__gift"
                    >
                        <div class="order-detail-v1-returns__gift-meta">
                            <strong>{{ line.title || 'Sostituzione' }}</strong>
                            <span class="text-muted">×{{ line.qty }}</span>
                        </div>
                        <div v-if="line.ean"><code>{{ line.ean }}</code></div>
                    </li>
                </ul>
            </section>
        </div>

        <div
            v-if="selection"
            class="modal fade in"
            style="display: block;"
            role="dialog"
            data-testid="return-action-dialog"
        >
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <button type="button" class="close" @click="selection = null"><span>&times;</span></button>
                        <h4 class="modal-title">{{ selection.label }} · {{ selection.returnOrderId }}</h4>
                    </div>
                    <div class="modal-body">
                        <p>Importo: {{ selection.amount }} · Esito: {{ selection.label }}</p>
                        <div v-if="selection.outcome === 'refund'" class="panel panel-default" data-testid="return-paypal-notice">
                            <div class="panel-heading"><strong>Se il reso è PayPal</strong></div>
                            <div class="panel-body">
                                <p>Il rimborso PayPal non è ancora disponibile</p>
                                <button type="button" class="btn btn-sm btn-default" @click="selection.outcome = 'archive'; selection.label = 'Archivia'">
                                    Archivia
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button
                            type="button"
                            class="btn btn-primary"
                            :disabled="busy || (selection.outcome === 'refund' && selection.isPayPal)"
                            @click="confirmOutcome"
                        >
                            Conferma
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="selection" class="modal-backdrop fade in"></div>
    </div>
</template>

<script>
import { formatDate } from './orderDetailHelpers';

const GIFT_KIND_LABELS = {
    return_gift: 'Emessa da reso',
    redemption: 'Riscatto checkout',
    'Riscatto checkout': 'Riscatto checkout',
    'Emessa da reso': 'Emessa da reso',
};

const HIDDEN_MARKERS = ['#SOSTITUZIONE#', 'WEBGIFTR'];

const ACTION_LABELS = {
    archivia: 'Archivia',
    archivia_rimborsa: 'Archivia e Rimborsa',
    archivia_gift: 'Archivia e Gift',
    approve_request: 'Approva richiesta',
    reject_request: 'Rifiuta richiesta',
};

export default {
    name: 'OrderReturnsCard',
    props: {
        order: { type: Object, required: true },
    },
    data() {
        return {
            busy: false,
            errorMessage: null,
            successMessage: null,
            selection: null,
        };
    },
    computed: {
        returns() {
            return this.order.returns || { inProgressCount: 0, items: [] };
        },
        label() {
            return (this.order.ui && this.order.ui.labels && this.order.ui.labels.returns)
                || 'Resi e rimborsi';
        },
        endpoints() {
            return this.order.endpoints || {};
        },
        giftCards() {
            const cards = this.order.giftCards && this.order.giftCards.cards;
            return Array.isArray(cards) ? cards : [];
        },
        substitutionLines() {
            const lines = this.order.giftCards && this.order.giftCards.substitutionLines;
            if (!Array.isArray(lines)) {
                return [];
            }

            return lines.map((line) => ({
                ...line,
                title: this.cleanTitle(line.title),
            }));
        },
        giftSaldo() {
            const stated = this.order.giftCards && this.order.giftCards.saldoFormatted;
            const sum = this.giftCards.reduce((total, card) => total + this.parseMoney(card.amountFormatted), 0);
            if (sum > 0) {
                return '€ ' + sum.toFixed(2).replace('.', ',');
            }

            return stated || '€ 0,00';
        },
    },
    methods: {
        formatDate,
        giftKindLabel(kind) {
            if (!kind) {
                return 'Gift card';
            }

            return GIFT_KIND_LABELS[kind] || String(kind).replace(/_/g, ' ');
        },
        cleanTitle(title) {
            if (!title) {
                return '';
            }

            return HIDDEN_MARKERS.reduce(
                (value, marker) => value.replace(marker, '').trim(),
                String(title),
            );
        },
        parseMoney(formatted) {
            if (!formatted) {
                return 0;
            }
            const normalized = String(formatted).replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
            const value = Number(normalized);

            return Number.isFinite(value) ? value : 0;
        },
        actionLabel(action) {
            return ACTION_LABELS[action] || action;
        },
        openOutcome(item, outcome, label) {
            this.selection = {
                outcome: outcome,
                label: label,
                returnOrderId: item.returnOrderId,
                amount: item.amountPaidFormatted || '—',
                isPayPal: !!item.isPayPal,
            };
        },
        async confirmOutcome() {
            if (!this.selection) {
                return;
            }
            this.busy = true;
            this.errorMessage = null;
            try {
                await this.postJson(this.endpoints.archiveReturn, {
                    return_num_prenotazione: Number(this.selection.returnOrderId),
                    outcome: this.selection.outcome,
                });
                window.location.reload();
            } catch (err) {
                this.errorMessage = (err && err.message) || 'Operazione fallita.';
                this.selection = null;
            } finally {
                this.busy = false;
            }
        },
        async runAction(item, action) {
            this.errorMessage = null;
            this.successMessage = null;
            this.busy = true;
            try {
                if (['archivia', 'archivia_rimborsa', 'archivia_gift'].includes(action)) {
                    await this.postJson(this.endpoints.archiveReturn, {
                        return_num_prenotazione: Number(item.returnOrderId),
                    });
                    this.successMessage = 'Reso archiviato.';
                } else if (action === 'approve_request' || action === 'reject_request') {
                    await this.postJson(this.endpoints.returnRequest, {
                        codice_riga: Number(item.codiceRiga),
                        decision: action === 'approve_request' ? 'approve' : 'reject',
                    });
                    this.successMessage = 'Richiesta aggiornata.';
                } else {
                    throw new Error('Azione non supportata: ' + action);
                }
                window.setTimeout(() => window.location.reload(), 600);
            } catch (err) {
                this.errorMessage = (err && err.message) || 'Operazione fallita.';
            } finally {
                this.busy = false;
            }
        },
        async postJson(url, body) {
            if (!url) {
                throw new Error('Endpoint non configurato.');
            }
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRF-TOKEN': this.order.csrfToken || '',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify(body),
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || payload.success === false) {
                throw new Error(payload.message || ('HTTP ' + response.status));
            }
            return payload;
        },
    },
};
</script>

<style scoped>
.order-detail-v1-returns__item + .order-detail-v1-returns__item {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #eee;
}
.order-detail-v1-returns__stack {
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid #f5c96a;
}
.order-detail-v1-returns__stack-title {
    margin: 0 0 10px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #777;
}
.order-detail-v1-returns__gift + .order-detail-v1-returns__gift {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #eee;
}
.order-detail-v1-returns__line + .order-detail-v1-returns__line {
    margin-top: 8px;
}
.order-detail-v1-returns__gift-code code {
    display: inline-block;
    white-space: nowrap;
}
.order-detail-v1-returns__gift-meta {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    margin: 4px 0;
}
</style>
